import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Stack, Typography, Paper, TextField, Button, Box, Chip,
  Table, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, InputAdornment, Link as MuiLink, Divider,
  Tabs, Tab, Checkbox,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNewOutlined";
import CheckIcon from "@mui/icons-material/CheckOutlined";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { apiGet, apiPost, apiDelete, ApiError } from "../api/client";
import ImageGalleryCell from "../components/ImageGalleryCell";
import { useToast } from "../ui/Toaster";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (unix) => unix ? new Date(Number(unix) * 1000).toLocaleDateString() : "—";

function ScoreChip({ score }) {
  const pct = Math.round((Number(score) || 0) * 100);
  if (score >= 0.7) return <Chip size="small" label={`${pct}%`} color="success" variant="outlined" />;
  if (score >= 0.35) return <Chip size="small" label={`${pct}%`} color="warning" variant="outlined" />;
  return <Chip size="small" label={`${pct}%`} variant="outlined" />;
}

function CandidateRow({ candidate, onApprove, busy, selected, onToggleSelect, anyBusy }) {
  const spend =
    (Number(candidate.cost) || 0) +
    (Number(candidate.tax_cost) || 0) +
    (Number(candidate.premium_fee) || 0);
  return (
    <TableRow
      hover
      selected={selected}
      sx={{ cursor: 'pointer' }}
      onClick={() => onToggleSelect?.(candidate.lot_id)}
    >
      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          size="small"
          checked={!!selected}
          onChange={() => onToggleSelect?.(candidate.lot_id)}
          disabled={anyBusy}
          inputProps={{ 'aria-label': `select ${candidate.lot_id}` }}
        />
      </TableCell>
      <TableCell sx={{ width: 72 }}>
        <ImageGalleryCell imageUrlsStr={candidate.image_urls} thumbnailSize={48} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>
          {candidate.cleaned_title || candidate.title || "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
          {candidate.lot_id}
          {candidate.condition && <Box component="span" sx={{ ml: 1 }}>{candidate.condition}</Box>}
          {candidate.location ? (
            <Box component="span" sx={{ ml: 1 }}>@ {candidate.location}</Box>
          ) : (
            // Unlocated lots are now included in candidates (used to be
            // silently filtered out). Surface the state inline so the
            // operator can spot at a glance which suggestions have no
            // physical confirmation behind them.
            <Chip
              size="small"
              label="no location"
              color="warning"
              variant="outlined"
              sx={{ ml: 1, height: 16, fontSize: 10 }}
            />
          )}
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
          {spend > 0 && <Typography variant="caption" color="text.secondary">Cost {money(spend)}</Typography>}
          {candidate.value > 0 && <Typography variant="caption" color="text.secondary">Value {money(candidate.value)}</Typography>}
          {candidate.purchased_at && <Typography variant="caption" color="text.secondary">{candidate.purchased_at}</Typography>}
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ width: 120 }}>
        <ScoreChip score={candidate.score} />
      </TableCell>
      <TableCell align="right" sx={{ width: 140 }} onClick={(e) => e.stopPropagation()}>
        <Button
          variant="contained"
          size="small"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CheckIcon fontSize="small" />}
          disabled={busy || anyBusy}
          onClick={onApprove}
        >
          Approve
        </Button>
      </TableCell>
    </TableRow>
  );
}

function OrderCard({ order, onLinked, onDismissed }) {
  const { error: toastError, success: toastSuccess, info: toastInfo } = useToast();
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [override, setOverride] = useState("");
  const [linking, setLinking] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const [opened, setOpened] = useState(false);
  // Multi-select for qty>1 orders. Pick N candidate lots and approve them
  // as one sale via /api/link_sale_lot { lot_ids: [...] }.
  const [selectedLots, setSelectedLots] = useState(() => new Set());
  const [bulkLinking, setBulkLinking] = useState(false);

  const cardKey = `${order.kind}-${order.id}`;

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ kind: order.kind, id: String(order.id) });
      const js = await apiGet(`/api/match_candidates?${params.toString()}`);
      setCandidates(Array.isArray(js?.results) ? js.results : []);
    } catch (e) {
      toastError("Couldn't load candidates", e instanceof ApiError ? e.message : String(e));
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [order.kind, order.id, toastError]);

  const open = () => {
    setOpened(true);
    if (candidates === null) fetchMatches();
  };

  const linkLot = async (lotId) => {
    if (!lotId) return;
    setLinking(lotId);
    try {
      const res = await apiPost("/api/link_sale_lot", {
        kind: order.kind,
        id: order.id,
        lot_id: lotId.trim(),
      });
      if (res?.success) {
        toastSuccess("Linked", `Sale #${res.sale_id ?? order.id} → ${lotId}`);
        onLinked?.(cardKey);
      } else {
        toastError("Couldn't link", res?.error || "Unknown error");
      }
    } catch (e) {
      toastError("Couldn't link", e instanceof ApiError ? e.message : String(e));
    } finally {
      setLinking(null);
    }
  };

  const toggleSelect = (lotId) => {
    setSelectedLots((prev) => {
      const n = new Set(prev);
      if (n.has(lotId)) n.delete(lotId); else n.add(lotId);
      return n;
    });
  };

  const linkSelectedLots = async () => {
    const lots = Array.from(selectedLots);
    if (lots.length === 0) return;
    if (lots.length === 1) return linkLot(lots[0]);  // reuse single-lot path
    setBulkLinking(true);
    try {
      const res = await apiPost("/api/link_sale_lot", {
        kind: order.kind,
        id: order.id,
        lot_ids: lots,
      });
      if (res?.success) {
        toastSuccess(
          `Linked ${lots.length} lots`,
          `Sale #${res.sale_id ?? order.id} → ${lots.join(", ")}`,
        );
        onLinked?.(cardKey);
      } else {
        toastError("Couldn't link", res?.error || "Unknown error");
      }
    } catch (e) {
      toastError("Couldn't link", e instanceof ApiError ? e.message : String(e));
    } finally {
      setBulkLinking(false);
    }
  };

  const dismiss = async () => {
    // Only pending rows can be deleted server-side; orphan rows just hide locally.
    if (order.kind !== "pending") {
      onDismissed?.(cardKey, { local: true });
      return;
    }
    if (!window.confirm("Delete this queued order entirely? This cannot be undone.")) return;
    setDismissing(true);
    try {
      await apiDelete(`/api/pending_sales/${encodeURIComponent(order.id)}`);
      toastInfo("Removed from queue");
      onDismissed?.(cardKey, { local: false });
    } catch (e) {
      toastError("Couldn't dismiss", e instanceof ApiError ? e.message : String(e));
    } finally {
      setDismissing(false);
    }
  };

  const ebayLink =
    order.order_id && String(order.sold_at || "").toLowerCase().includes("ebay")
      ? `https://www.ebay.com/mesh/ord/details?orderid=${encodeURIComponent(order.order_id.trim())}`
      : null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
            {order.sold_at && <Chip size="small" label={order.sold_at} variant="outlined" />}
            <Chip
              size="small"
              label={order.kind === "pending" ? "Lot collision" : "No lot linked"}
              color={order.kind === "pending" ? "warning" : "default"}
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">{fmtDate(order.sold_date)}</Typography>
            {order.order_id && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                #{order.order_id}
              </Typography>
            )}
            {ebayLink && (
              <MuiLink
                href={ebayLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open on eBay"
                sx={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </MuiLink>
            )}
          </Stack>
          <Typography variant="subtitle1" sx={{ mt: 0.75, fontWeight: 600 }}>
            {order.title || "(no title)"}
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 0.5, rowGap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Sold {money(order.sold_price)}</Typography>
            {order.shipping_cost > 0 && (
              <Typography variant="caption" color="text.secondary">Ship {money(order.shipping_cost)}</Typography>
            )}
            {order.platform_fee > 0 && (
              <Typography variant="caption" color="text.secondary">Fee {money(order.platform_fee)}</Typography>
            )}
            {order.requested_lot_id && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                tried: {order.requested_lot_id}
              </Typography>
            )}
          </Stack>
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          {!opened ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<AutoAwesomeIcon fontSize="small" />}
              onClick={open}
            >
              Find matches
            </Button>
          ) : (
            <Button variant="text" size="small" onClick={() => setOpened(false)}>
              Hide
            </Button>
          )}
        </Box>
      </Stack>

      {opened && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />

          {loading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1.5, color: 'text.secondary' }}>
              <CircularProgress size={14} />
              <Typography variant="body2">Searching inventory…</Typography>
            </Stack>
          )}

          {!loading && candidates && candidates.length === 0 && (
            <Stack alignItems="center" gap={1} sx={{ py: 4, color: 'text.secondary' }}>
              <SearchIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>No similar items in inventory</Typography>
              <Typography variant="caption">
                Nothing on hand looks like this. Try the manual override below.
              </Typography>
            </Stack>
          )}

          {!loading && candidates && candidates.length > 0 && (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
                <Table size="small">
                  <TableBody>
                    {candidates.map((c) => (
                      <CandidateRow
                        key={c.lot_id}
                        candidate={c}
                        busy={linking === c.lot_id}
                        anyBusy={bulkLinking}
                        selected={selectedLots.has(c.lot_id)}
                        onToggleSelect={toggleSelect}
                        onApprove={() => linkLot(c.lot_id)}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* Bulk-approve footer — only relevant when the buyer
                  ordered qty>1 of one product. The "Approve" button on
                  each row stays for the qty=1 quick path. */}
              {selectedLots.size > 1 && (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, p: 1.25, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedLots.size} lots selected
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Will be linked to one sale (#{order.id})
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setSelectedLots(new Set())}
                    disabled={bulkLinking}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={linkSelectedLots}
                    disabled={bulkLinking}
                    startIcon={
                      bulkLinking
                        ? <CircularProgress size={14} color="inherit" />
                        : <CheckIcon fontSize="small" />
                    }
                  >
                    Approve {selectedLots.size}
                  </Button>
                </Stack>
              )}
            </>
          )}

          {/* Manual override */}
          <Box sx={{ pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', display: 'block', mb: 1 }}>
              Manual lot id
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Type a lot id…"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                disabled={!override.trim() || linking === override.trim()}
                onClick={() => linkLot(override)}
                startIcon={
                  linking === override.trim()
                    ? <CircularProgress size={14} color="inherit" />
                    : <LinkIcon fontSize="small" />
                }
              >
                Link
              </Button>
            </Stack>
          </Box>

          <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1.5 }}>
            <Button
              variant="text"
              size="small"
              color="inherit"
              disabled={dismissing}
              startIcon={
                dismissing
                  ? <CircularProgress size={14} color="inherit" />
                  : (order.kind === "pending"
                      ? <DeleteOutlineIcon fontSize="small" />
                      : <CloseIcon fontSize="small" />)
              }
              onClick={dismiss}
            >
              {order.kind === "pending" ? "Delete from queue" : "Hide for now"}
            </Button>
          </Stack>
        </Box>
      )}
    </Paper>
  );
}

// Listing match candidate row — the eBay listing side. Same shape concept as
// CandidateRow but sourced from the local listings table.
function ListingCandidateRow({ candidate, onApprove, busy }) {
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>
          {candidate.title || "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
          item {candidate.ebay_item_id}
          {candidate.sku && <Box component="span" sx={{ ml: 1 }}>SKU {candidate.sku}</Box>}
          {candidate.condition_enum && <Box component="span" sx={{ ml: 1 }}>{candidate.condition_enum}</Box>}
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
          {candidate.price != null && <Typography variant="caption" color="text.secondary">{money(candidate.price)}</Typography>}
          {candidate.quantity != null && <Typography variant="caption" color="text.secondary">qty {candidate.quantity}</Typography>}
          {candidate.existing_lot_id && (
            <Typography variant="caption" color="warning.main">currently linked to {candidate.existing_lot_id}</Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ width: 120 }}>
        <ScoreChip score={candidate.score} />
      </TableCell>
      <TableCell align="right" sx={{ width: 140 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CheckIcon fontSize="small" />}
          disabled={busy}
          onClick={onApprove}
        >
          Link
        </Button>
      </TableCell>
    </TableRow>
  );
}

// "I have an inventory item that should be linked to an active eBay listing"
// — mirror of OrderCard but inverted: the inventory item is the anchor, and
// candidates are listings.
function InventoryCard({ item, onLinked, onDismissed }) {
  const { error: toastError, success: toastSuccess } = useToast();
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [override, setOverride] = useState("");
  const [linking, setLinking] = useState(null);
  const [opened, setOpened] = useState(false);

  const cardKey = `inv-${item.lot_id}`;

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ lot_id: item.lot_id });
      const js = await apiGet(`/api/listing_match_candidates?${params.toString()}`);
      setCandidates(Array.isArray(js?.results) ? js.results : []);
    } catch (e) {
      toastError("Couldn't load listings", e instanceof ApiError ? e.message : String(e));
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [item.lot_id, toastError]);

  const open = () => {
    setOpened(true);
    if (candidates === null) fetchMatches();
  };

  const linkListing = async (ebayItemId) => {
    if (!ebayItemId) return;
    setLinking(ebayItemId);
    try {
      const res = await apiPost("/api/link_listing", {
        ebay_item_id: ebayItemId.trim(),
        lot_id: item.lot_id,
      });
      if (res?.success) {
        toastSuccess("Linked", `${item.lot_id} → ${ebayItemId}`);
        onLinked?.(cardKey);
      } else {
        toastError("Couldn't link", res?.error || "Unknown error");
      }
    } catch (e) {
      toastError("Couldn't link", e instanceof ApiError ? e.message : String(e));
    } finally {
      setLinking(null);
    }
  };

  const spend =
    (Number(item.cost) || 0) +
    (Number(item.tax_cost) || 0) +
    (Number(item.premium_fee) || 0);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
        <Box sx={{ flexShrink: 0 }}>
          <ImageGalleryCell imageUrlsStr={item.image_urls} thumbnailSize={64} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
            {item.purchased_at && <Chip size="small" label={item.purchased_at} variant="outlined" />}
            <Chip
              size="small"
              label={item.listed ? "Marked listed, no link" : "Not yet listed"}
              color={item.listed ? "warning" : "default"}
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">{fmtDate(item.purchase_date)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{item.lot_id}</Typography>
          </Stack>
          <Typography variant="subtitle1" sx={{ mt: 0.75, fontWeight: 600 }}>
            {item.cleaned_title || item.title || "(no title)"}
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 0.5, rowGap: 0.5 }}>
            {spend > 0 && <Typography variant="caption" color="text.secondary">Cost {money(spend)}</Typography>}
            {item.value > 0 && <Typography variant="caption" color="text.secondary">Value {money(item.value)}</Typography>}
            {item.location && <Typography variant="caption" color="text.secondary">@ {item.location}</Typography>}
            {item.condition && <Typography variant="caption" color="text.secondary">{item.condition}</Typography>}
          </Stack>
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          {!opened ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<AutoAwesomeIcon fontSize="small" />}
              onClick={open}
            >
              Find listing
            </Button>
          ) : (
            <Button variant="text" size="small" onClick={() => setOpened(false)}>
              Hide
            </Button>
          )}
        </Box>
      </Stack>

      {opened && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />

          {loading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1.5, color: 'text.secondary' }}>
              <CircularProgress size={14} />
              <Typography variant="body2">Searching listings…</Typography>
            </Stack>
          )}

          {!loading && candidates && candidates.length === 0 && (
            <Stack alignItems="center" gap={1} sx={{ py: 4, color: 'text.secondary' }}>
              <SearchIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>No similar active listings</Typography>
              <Typography variant="caption" sx={{ textAlign: 'center', maxWidth: 360 }}>
                Listings are only known after the Listings page syncs them.
                Use manual override below if you know the eBay item id.
              </Typography>
            </Stack>
          )}

          {!loading && candidates && candidates.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableBody>
                  {candidates.map((c) => (
                    <ListingCandidateRow
                      key={c.ebay_item_id}
                      candidate={c}
                      busy={linking === c.ebay_item_id}
                      onApprove={() => linkListing(c.ebay_item_id)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', display: 'block', mb: 1 }}>
              Manual eBay item id
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="eBay item id…"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                disabled={!override.trim() || linking === override.trim()}
                onClick={() => linkListing(override)}
                startIcon={
                  linking === override.trim()
                    ? <CircularProgress size={14} color="inherit" />
                    : <LinkIcon fontSize="small" />
                }
              >
                Link
              </Button>
            </Stack>
          </Box>

          <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1.5 }}>
            <Button
              variant="text"
              size="small"
              color="inherit"
              startIcon={<CloseIcon fontSize="small" />}
              onClick={() => onDismissed?.(cardKey)}
            >
              Hide for now
            </Button>
          </Stack>
        </Box>
      )}
    </Paper>
  );
}

export default function MatchOrders() {
  const { error: toastError } = useToast();
  const [tab, setTab] = useState("orders");

  // Orders tab state (existing).
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(() => new Set());
  const [search, setSearch] = useState("");

  // Listings tab state.
  const [invItems, setInvItems] = useState([]);
  const [invLoading, setInvLoading] = useState(true);
  const [invHidden, setInvHidden] = useState(() => new Set());
  const [invSearch, setInvSearch] = useState("");
  const [retailers, setRetailers] = useState([]);
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [includeUnlisted, setIncludeUnlisted] = useState(false);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const js = await apiGet("/api/pending_sales", { signal });
      setOrders(Array.isArray(js?.results) ? js.results : []);
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load orders", e instanceof ApiError ? e.message : String(e));
      setOrders([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [toastError]);

  const retailerKey = useMemo(() => selectedRetailers.slice().sort().join(","), [selectedRetailers]);

  const loadInv = useCallback(async (signal) => {
    setInvLoading(true);
    try {
      const params = new URLSearchParams();
      if (retailerKey) params.set("source", retailerKey);
      if (includeUnlisted) params.set("include_unlisted", "true");
      const url = "/api/unlinked_inventory_items" + (params.toString() ? `?${params.toString()}` : "");
      const js = await apiGet(url, { signal });
      setInvItems(Array.isArray(js?.results) ? js.results : []);
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load unlinked inventory", e instanceof ApiError ? e.message : String(e));
      setInvItems([]);
    } finally {
      if (!signal?.aborted) setInvLoading(false);
    }
  }, [retailerKey, includeUnlisted, toastError]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    loadInv(ctrl.signal);
    return () => ctrl.abort();
  }, [load, loadInv]);

  // Retailer chip choices — small payload, fetched once.
  useEffect(() => {
    let cancelled = false;
    apiGet("/api/inventory/retailers").then((data) => {
      if (!cancelled) setRetailers(Array.isArray(data) ? data : []);
    }).catch(() => { /* silent — non-critical */ });
    return () => { cancelled = true; };
  }, []);

  const onLinked = useCallback((cardKey) => {
    setOrders((prev) => prev.filter((o) => `${o.kind}-${o.id}` !== cardKey));
  }, []);

  const onDismissed = useCallback((cardKey, { local }) => {
    if (local) {
      setHidden((prev) => {
        const n = new Set(prev);
        n.add(cardKey);
        return n;
      });
    } else {
      setOrders((prev) => prev.filter((o) => `${o.kind}-${o.id}` !== cardKey));
    }
  }, []);

  const onInvLinked = useCallback((cardKey) => {
    setInvItems((prev) => prev.filter((it) => `inv-${it.lot_id}` !== cardKey));
  }, []);

  const onInvDismissed = useCallback((cardKey) => {
    setInvHidden((prev) => {
      const n = new Set(prev);
      n.add(cardKey);
      return n;
    });
  }, []);

  const invTerm = invSearch.trim().toLowerCase();
  const invVisible = invItems
    .filter((it) => !invHidden.has(`inv-${it.lot_id}`))
    .filter((it) =>
      !invTerm
        ? true
        : (it.title || "").toLowerCase().includes(invTerm) ||
          (it.cleaned_title || "").toLowerCase().includes(invTerm) ||
          (it.lot_id || "").toLowerCase().includes(invTerm)
    );

  const term = search.trim().toLowerCase();
  const visible = orders
    .filter((o) => !hidden.has(`${o.kind}-${o.id}`))
    .filter((o) =>
      !term
        ? true
        : (o.title || "").toLowerCase().includes(term) ||
          (o.order_id || "").toLowerCase().includes(term) ||
          (o.sold_at || "").toLowerCase().includes(term) ||
          (o.requested_lot_id || "").toLowerCase().includes(term)
    );

  const pendingCount = orders.filter((o) => o.kind === "pending").length;
  const orphanCount  = orders.filter((o) => o.kind === "orphan").length;

  const subtitle = loading
    ? "Loading…"
    : `${visible.length} order${visible.length === 1 ? "" : "s"} need a lot` +
      (pendingCount ? ` • ${pendingCount} from collisions` : "") +
      (orphanCount ? ` • ${orphanCount} orphan${orphanCount === 1 ? "" : "s"}` : "") +
      (hidden.size ? ` • ${hidden.size} hidden` : "");

  return (
    <Box>
      {/* Page header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
            Match
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {tab === "orders" ? subtitle :
              invLoading ? "Loading…" :
              `${invVisible.length} item${invVisible.length === 1 ? "" : "s"} need a listing`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={(tab === "orders" ? loading : invLoading) ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={() => (tab === "orders" ? load() : loadInv())}
            disabled={tab === "orders" ? loading : invLoading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 1 }}
        >
          <Tab
            value="orders"
            label={`Sales → Inventory${orders.length ? ` (${orders.length})` : ""}`}
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
          <Tab
            value="listings"
            label={`Inventory → Listings${invItems.length ? ` (${invItems.length})` : ""}`}
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
        </Tabs>
      </Paper>

      {tab === "orders" && (
        <>
          {/* Search */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search title, order id, lot id, platform…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>

          {!loading && visible.length === 0 && (
            <Paper variant="outlined" sx={{ p: 4 }}>
              <Stack alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                <CheckIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {orders.length === 0 ? "Nothing queued" : "All caught up"}
                </Typography>
                <Typography variant="caption" sx={{ textAlign: 'center', maxWidth: 480 }}>
                  {orders.length === 0
                    ? "When a sale fails because the lot is already linked elsewhere, it lands here for matching."
                    : "Either you've linked everything, or the rest are filtered out."}
                </Typography>
                {hidden.size > 0 && (
                  <Button variant="text" size="small" onClick={() => setHidden(new Set())} sx={{ mt: 1 }}>
                    Show {hidden.size} hidden
                  </Button>
                )}
              </Stack>
            </Paper>
          )}

          <Stack spacing={1.5}>
            {visible.map((order) => (
              <OrderCard
                key={`${order.kind}-${order.id}`}
                order={order}
                onLinked={onLinked}
                onDismissed={onDismissed}
              />
            ))}
          </Stack>
        </>
      )}

      {tab === "listings" && (
        <>
          {/* Filters */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack spacing={1.5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search title or lot id…"
                value={invSearch}
                onChange={(e) => setInvSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              {retailers.length > 0 && (
                <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                  <Typography variant="caption" sx={{ color: "text.secondary", mr: 0.5 }}>
                    Retailer:
                  </Typography>
                  {retailers.map((r) => {
                    const active = selectedRetailers.includes(r.name);
                    return (
                      <Chip
                        key={r.name}
                        size="small"
                        label={`${r.name} (${r.count})`}
                        variant={active ? "filled" : "outlined"}
                        color={active ? "primary" : "default"}
                        onClick={() =>
                          setSelectedRetailers((prev) =>
                            prev.includes(r.name)
                              ? prev.filter((x) => x !== r.name)
                              : [...prev, r.name]
                          )
                        }
                      />
                    );
                  })}
                  {selectedRetailers.length > 0 && (
                    <Chip
                      size="small"
                      label="Clear"
                      variant="outlined"
                      onClick={() => setSelectedRetailers([])}
                      sx={{ ml: 0.5 }}
                    />
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Chip
                    size="small"
                    label={includeUnlisted ? "Including unlisted" : "Only marked listed"}
                    variant={includeUnlisted ? "filled" : "outlined"}
                    color={includeUnlisted ? "primary" : "default"}
                    onClick={() => setIncludeUnlisted((v) => !v)}
                  />
                </Stack>
              )}
            </Stack>
          </Paper>

          {!invLoading && invVisible.length === 0 && (
            <Paper variant="outlined" sx={{ p: 4 }}>
              <Stack alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                <CheckIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {invItems.length === 0 ? "Everything is linked" : "All caught up"}
                </Typography>
                <Typography variant="caption" sx={{ textAlign: 'center', maxWidth: 480 }}>
                  {invItems.length === 0
                    ? "Every active inventory item has a linked eBay listing — or none are marked listed yet."
                    : "Either you've linked everything visible, or the rest are filtered out."}
                </Typography>
                {invHidden.size > 0 && (
                  <Button variant="text" size="small" onClick={() => setInvHidden(new Set())} sx={{ mt: 1 }}>
                    Show {invHidden.size} hidden
                  </Button>
                )}
              </Stack>
            </Paper>
          )}

          <Stack spacing={1.5}>
            {invVisible.map((item) => (
              <InventoryCard
                key={`inv-${item.lot_id}`}
                item={item}
                onLinked={onInvLinked}
                onDismissed={onInvDismissed}
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}
